import { PauseRounded, PlayArrowRounded } from '@material-ui/icons'
import './styles.css'
import { useState, useRef, useEffect } from 'react'

export default function Video({ config }) {
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [isSeeking, setIsSeeking] = useState(false)
    const [seekTime, setSeekTime] = useState(null)
    const videoRef = useRef(null)
    const progressBarRef = useRef(null)

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        
        // Prevenir tela cheia automática
        video.setAttribute('webkit-playsinline', 'true');
        
        const updateTime = () => setCurrentTime(video.currentTime);
        const updateDuration = () => setDuration(video.duration || 0);
        const handlePause = () => setIsPlaying(false);
        const handlePlay = () => setIsPlaying(true);
        
        video.addEventListener('timeupdate', updateTime);
        video.addEventListener('durationchange', updateDuration);
        video.addEventListener('pause', handlePause);
        video.addEventListener('play', handlePlay);
        
        updateDuration();
        
        return () => {
            video.removeEventListener('timeupdate', updateTime);
            video.removeEventListener('durationchange', updateDuration);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('play', handlePlay);
        };
    }, []);

    useEffect(() => {
        if (!isSeeking) return;
        function handleMove(e) {
            updateSeek(e);
        }
        function handleUp(e) {
            if (seekTime !== null && videoRef.current) {
                videoRef.current.currentTime = seekTime;
                setCurrentTime(seekTime);
            }
            setIsSeeking(false);
            setSeekTime(null);
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        }
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [isSeeking, seekTime]);

    function handleToggleState() {
        setIsPlaying(prev => {
            const next = !prev;
            if (videoRef.current) {
                if (next) {
                    // Pause all other videos
                    const allVideos = document.querySelectorAll('.video-container video');
                    allVideos.forEach(v => {
                        if (v !== videoRef.current) v.pause();
                    });
                    videoRef.current.play();
                } else {
                    videoRef.current.pause();
                }
            }
            return next;
        })
    }

    function getBarPercent(e) {
        const bar = progressBarRef.current;
        if (!bar) return 0;
        const rect = bar.getBoundingClientRect();
        const x = e.touches ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
        return Math.max(0, Math.min(1, x / rect.width));
    }

    function handleProgressBarClick(e) {
        if (!videoRef.current) return;
        const percent = getBarPercent(e);
        const newTime = percent * duration;
        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    }

    function handleProgressBarMouseDown(e) {
        setIsSeeking(true);
        updateSeek(e);
    }

    function updateSeek(e) {
        const percent = getBarPercent(e);
        const newTime = percent * duration;
        setSeekTime(newTime);
    }

    function handleProgressBarMouseMove(e) {
        if (!isSeeking) return;
        updateSeek(e);
    }

    function handleProgressBarMouseUp(e) {
        if (seekTime !== null && videoRef.current) {
            videoRef.current.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
        setIsSeeking(false);
        setSeekTime(null);
        window.removeEventListener('mousemove', handleProgressBarMouseMove);
        window.removeEventListener('mouseup', handleProgressBarMouseUp);
    }

    function formatTime(sec) {
        if (!isFinite(sec)) return '00:00';
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }

    const progress = duration ? ((isSeeking && seekTime !== null ? seekTime : currentTime) / duration) * 100 : 0;

    return (
        <div className="video-container" style={{ ...(config.style || {}) }}>
            <PlayPauseButton onPress={handleToggleState} state={isPlaying} />
            <video 
                ref={videoRef} 
                onClick={handleToggleState}
                playsInline={true}
                preload="metadata"
                {...(config.banner && { poster: config.banner })}
            >
                <source src={config.src}></source>
            </video>
            <div className="video-progressbar-container">
                <div className='time-display'>
                    <p className="video-time video-time-current">{formatTime(isSeeking && seekTime !== null ? seekTime : currentTime)}</p>
                    <p className="video-time video-time-duration">{formatTime(duration)}</p>

                </div>
                <div
                    className="video-progressbar"
                    ref={progressBarRef}
                    onMouseDown={handleProgressBarMouseDown}
                    onClick={handleProgressBarClick}
                >
                    <div
                        className="video-progressbar-fill"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>
        </div>
    )
}


function PlayPauseButton({ state, onPress }) {
    if (state) return null

    return (
        <div className='play-pause' onClick={onPress}>
            {
                !state ? <PlayArrowRounded color="white" /> : <PauseRounded color="white" />
            }
        </div>
    )
}